import React from "react";
import { Phone } from "lucide-react";
import { Business } from "../../types";
import { getContrastTextColor } from "../../lib/color-utils";

interface IdentityHubProps {
  business: Business | null | undefined;
  primaryColor: string;
}

/**
 * Identity Hub (Sovereign V221.11 - "Sleek Bar" Refactor)
 *
 * Centralized branding component for the Hajez Sovereign portal.
 * Features:
 * - Ultra-Compact Horizontal Flow (Row-first on all devices)
 * - Gradient Depth System (Replaces solid slabs)
 * - Integrated Identity Group (Logo/Seal + Name)
 * - Sophisticated Rounding Refinement
 */
export const IdentityHub: React.FC<IdentityHubProps> = ({
  business,
  primaryColor,
}) => {
  if (!business) return null;

  const textColor = getContrastTextColor(primaryColor);
  const hasLogo = !!business.logo_url;
  const businessInitial = business.name?.charAt(0).toUpperCase() || "B";

  return (
    <div
      className="w-full rounded-2xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)] border border-white/10 overflow-hidden mb-6 md:mb-10 transition-all duration-500"
      id="krude-identity-hub"
      style={{
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
        color: textColor,
      }}
    >
      <div className="px-4 py-3 md:px-8 md:py-4 flex flex-row items-center justify-between gap-4 min-h-[70px] md:min-h-[100px]">
        {/* Identity Group (Horizontal Pair) */}
        <div className="flex flex-row items-center gap-3 md:gap-6 min-w-0">
          {hasLogo ? (
            <div className="w-11 h-11 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center bg-transparent rounded-xl md:rounded-2xl p-1.5 transition-transform hover:scale-105">
              <img
                src={business.logo_url || undefined}
                alt={business.name}
                className="w-full h-full object-contain filter drop-shadow-sm"
              />
            </div>
          ) : (
            <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 rounded-full flex items-center justify-center text-lg md:text-2xl font-black bg-white/20 backdrop-blur-md border border-white/30 shadow-[inset_0_2px_4px_rgba(255,255,255,0.3)]">
              {businessInitial}
            </div>
          )}

          <div className="flex flex-col justify-center min-w-0">
            <h1 className="text-sm md:text-2xl font-sovereign-header uppercase leading-tight tracking-wider truncate font-black">
              {business.name}
            </h1>
            <p className="text-[8px] md:text-[10px] uppercase tracking-[0.3em] opacity-60 font-bold hidden xs:block">
              Official Booking Hub
            </p>
          </div>
        </div>

        {/* Action Node (Icon on Mobile, Text on Desktop) */}
        <div className="flex-shrink-0">
          {business.phone_number && (
            <a
              href={`tel:${business.phone_number.replace(/\s+/g, "")}`}
              className="bg-white rounded-lg h-11 px-4 md:px-6 flex items-center gap-2 shadow-lg shadow-black/5 transition-all hover:scale-105 active:scale-95 cursor-pointer no-underline group/phone border border-slate-100"
              style={{
                color: textColor === "#1e293b" ? "#0f172a" : primaryColor,
              }}
              title={`Call ${business.name}`}
            >
              <Phone
                size={16}
                className="md:size-3.5 group-hover/phone:animate-pulse"
                strokeWidth={2.5}
              />
              <span className="text-[11px] font-black uppercase tracking-widest hidden md:block">
                {business.phone_number}
              </span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
